<template>
  <div
    class="st-table"
    :class="{ 'st-table--compact': compact }"
    :data-set-breakpoint="tableBreakpoint"
  >
    <div class="st-table__mobile-sort">
      <st-table-mobile-sort
        v-if="hasSortableHeaders"
        :headers="headers"
        @sort="handleSortClick"
      />
      <div v-if="selectable" class="st-table__mobile-check-all">
        <StCheckbox
          aria-label="Markera alla"
          :model-value="allRowsChecked"
          :indeterminate="isIndeterminate"
          @update:model-value="handleHeaderCheckboxChange"
        >
          Markera alla
        </StCheckbox>
      </div>
    </div>

    <table class="st-table__content">
      <thead class="st-table__content__header">
        <tr class="st-table__content__header__row">
          <td
            v-if="selectable"
            class="st-table__content__header__row__checkbox"
          >
            <StCheckbox
              aria-label="Markera alla"
              :model-value="allRowsChecked"
              :indeterminate="isIndeterminate"
              @update:model-value="handleHeaderCheckboxChange"
            />
          </td>
          <st-table-header
            v-for="(header, i) in headers"
            :key="`${header.name}-${i}`"
            :header="header"
            :is-sorted="sortBy === header.sortBy"
            @sort="handleSortClick"
          />
        </tr>
      </thead>

      <tbody class="st-table__content__body">
        <template
          v-for="(data, index) in tableData"
          :key="`table-row-${index}`"
        >
          <tr
            :class="classListRow(data, index)"
          >
            <td
              v-if="selectable"
              class="st-table-item__data-cell"
              :style="{ '--status-color': data.status }"
              :class="{
                'st-table-item__data-cell--white-status':
                  data.status === '#FFFFFF',
              }"
            >
              <div class="st-table-item__data-cell__checkbox">
                <StCheckbox
                  aria-label="Markera denna rad"
                  :model-value="checkedRows[index]"
                  @update:model-value="
                    (value) => handleCheckboxChange(value, data, index)
                  "
                  @click.stop
                />
              </div>
            </td>

            <st-table-item
              v-for="(d, i) in data.items"
              :key="`${d.value}-${i}`"
              :data="d"
              :header="headers[i]"
              :border-color="data.status"
              @component-event="
                (event) => handleComponentEvent(event, data, index, d, i)
              "
            />
            <td
              v-if="expandableRows && data.expandableComponent"
              class="st-table-item__data-cell"
            >
              <div
                :class="classListChevron(isExpanded(data.id))"
                role="button"
                tabindex="0"
                :aria-expanded="isExpanded(data.id)"
                aria-label="För mer information, fäll ut rad nedanför"
                :aria-controls="`expandable-item-${data.id}`"
                @click.stop="toggleExpandedRow(data.id)"
                @keydown.enter.stop="toggleExpandedRow(data.id)"
              >
                <st-icon name="chevron" />
              </div>
            </td>
            <td v-else-if="clickable" class="st-table-item__data-cell">
              <div
                class="st-table__content__body__row__chevron st-table__content__body__row__chevron--right"
                role="button"
                tabindex="0"
                :aria-label="getRowClickAriaLabel(data, index)"
                @click.stop="handleRowClick(data, index)"
                @keydown.enter.stop="handleRowClick(data, index)"
              >
                <st-icon name="chevron" />
              </div>
            </td>
          </tr>
          <tr
            v-if="expandableRows && isExpanded(data.id)"
            :id="`expandable-item-${data.id}`"
            class="st-table__content__body__expanded-row"
            :style="{ '--status-color': data.status }"
          >
            <td
              :colspan="headers.length + (selectable ? 1 : 0) + 1"
            :class="{
              'st-table__content__body__expanded-row--white-status':
                data.status === '#FFFFFF',
              'st-table__content__body__expanded-row--last':
                index === tableData.length - 1,
            }"
            >
              <component
                :is="data.expandableComponent"
                v-bind="data.expandableComponentProps"
                v-on="getExpandableComponentListeners(data, index)"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import StTableItem from "./TableItem.vue";
import StTableHeader from "./TableHeader.vue";
import StTableMobileSort from "./TableMobileSort.vue";

import StIcon from "@/st-components/icon/Icon.vue";
import StCheckbox from "@/st-components/checkbox/Checkbox.vue";

const props = defineProps({
  tableData: {
    type: Array,
    validator: (items) => {
      const validItems = items.filter((item) => {
        if (!item.expandableRow) {
          return true;
        }
        return Object.prototype.hasOwnProperty.call(item, "id");
      });
      return validItems.length === items.length;
    },
  },
  headers: {
    type: Array,
  },
  tableBreakpoint: {
    type: String,
    default: "large",
  },
  clickable: {
    type: Boolean,
    default: false,
  },
  rowClickAriaLabel: {
    type: [String, Function],
    default: "Visa mer information om raden",
  },
  compact: {
    type: Boolean,
    default: false,
  },
  expandableRows: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
});

// Cell components configure event names at runtime through componentEmits.
// eslint-disable-next-line vue/valid-define-emits
const emit = defineEmits();

const expandedRowId = ref(null);
const sortBy = ref("");
const checkedRows = ref([]);

watch(
  () => props.tableData.map((row) => [row, row.checked]),
  (rows) => {
    checkedRows.value = rows.map(([, checked]) => !!checked);
  },
  { immediate: true },
);

const checkedCount = computed(
  () => checkedRows.value.filter(Boolean).length,
);

const allRowsChecked = computed(
  () =>
    props.tableData.length > 0 &&
    checkedCount.value === props.tableData.length,
);

const isIndeterminate = computed(
  () =>
    checkedCount.value > 0 && checkedCount.value < props.tableData.length,
);

const hasSortableHeaders = computed(() =>
  props.headers.some((header) => header.sortBy),
);

const isExpanded = (id) => expandedRowId.value === id;

const classListRow = (rowData, index) => {
  const row = "st-table__content__body__row";

  return [
    row,
    {
      [`${row}--is-danger`]: rowData.state === "danger",
      [`${row}--expanded`]: props.expandableRows && isExpanded(rowData.id),
      [`${row}--beige`]: index % 2,
      [`${row}--white`]: !(index % 2),
    },
  ];
};

const classListChevron = (isExpanded) => {
  const chevron = "st-table__content__body__row__chevron";
  return [chevron, `${chevron}--expanded-${isExpanded}`];
};

const toggleExpandedRow = (id) => {
  expandedRowId.value = expandedRowId.value === id ? null : id;
};

const handleSortClick = (sortingBy, order) => {
  sortBy.value = sortingBy;
  emit("sort", sortingBy, order);
};

const normalizeCheckboxValue = (value) =>
  value === true || value === "true" || value === "checked";

const handleHeaderCheckboxChange = (value) => {
  const checked = normalizeCheckboxValue(value);
  checkedRows.value = props.tableData.map(() => checked);

  emit("header-checked", {
    value: checked,
  });
};

const handleCheckboxChange = (value, data, index) => {
  const checked = normalizeCheckboxValue(value);
  checkedRows.value[index] = checked;

  emit("row-checked", {
    value: checked,
    rowData: data,
    rowIndex: index,
  });
};

const handleRowClick = (row, index) => {
  if (props.clickable) {
    emit("on-row-click", row, index);
  }
};

const eventValue = (values) => (values.length > 1 ? values : values[0]);

const handleComponentEvent = (
  { eventName, values },
  rowData,
  rowIndex,
  itemData,
  itemIndex,
) => {
  emit(eventName, {
    value: eventValue(values),
    rowData,
    rowIndex,
    itemData,
    itemIndex,
  });
};

const getExpandableComponentListeners = (rowData, rowIndex) => {
  const eventNames = Array.isArray(rowData.expandableComponentEmits)
    ? rowData.expandableComponentEmits
    : [rowData.expandableComponentEmits].filter(Boolean);

  return Object.fromEntries(
    eventNames.map((eventName) => [
      eventName,
      (...values) =>
        emit(eventName, {
          value: eventValue(values),
          rowData,
          rowIndex,
        }),
    ]),
  );
};

const getRowClickAriaLabel = (row, index) =>
  typeof props.rowClickAriaLabel === "function"
    ? props.rowClickAriaLabel(row, index)
    : props.rowClickAriaLabel;
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-table {
  font-family: var(--font-primary);
  font-size: rem-calc(16);
  color: var(--text-color-primary);

  &--compact {
    :deep(.st-table-header),
    :deep(.st-table-item__data-cell),
    .st-table__content__header__row__checkbox {
      padding: rem-calc(8);
    }

    .st-table-item__data-cell__checkbox,
    .st-table__content__body__row__chevron {
      padding: 0;
    }
  }

  &__mobile-sort {
    padding: rem-calc(10);
    display: block;
    @include set-breakpoint {
      display: none;
    }
  }

  &__mobile-check-all {
    margin: rem-calc(16) 0;
  }

  &__content {
    width: 100%;
    border-collapse: collapse;
    @include set-breakpoint {
      border-spacing: 0 rem-calc(8);
    }
    &__header {
      display: none;
      @include set-breakpoint {
        display: table-header-group;
      }
      &__row {
        border: solid var(--border-color-quinary) rem-calc(1);
        vertical-align: baseline;
        border: none;

        &__checkbox {
          padding: rem-calc(24);
          justify-items: center;
          align-content: center;

          & > :first-child {
            justify-content: center;
          }
        }
      }
    }
    &__body {
      &__row {
        background-color: var(--container-color-primary);
        display: flex;
        flex-direction: column;
        transition: 200ms linear background-color;
        position: relative;
        border: rem-calc(1) solid var(--disabled-color-secondary);
        border-bottom: none;

        & .st-table-item__data-cell {
          &__checkbox {
            padding: rem-calc(24);
            justify-items: flex-start;

            @include set-breakpoint {
              justify-items: center;
            }

            & > :first-child {
              justify-content: center;
            }
          }

          @include set-breakpoint {
            position: relative;
          }
        }

        & .st-table-item__data-cell:first-child::before {
          content: "";
          position: absolute;
          left: rem-calc(-8);
          top: rem-calc(-1);
          bottom: 0;
          width: rem-calc(8);
          background: var(--status-color);
          border-radius: rem-calc(4) 0 0 rem-calc(4);
        }

        & .st-table-item__data-cell--white-status:first-child::before {
          box-shadow: inset 0 0 0 rem-calc(1)
            var(--disabled-color-secondary);
        }

        &--expanded .st-table-item__data-cell:first-child::before {
          border-radius: rem-calc(4) 0 0 0;
        }

        &--expanded {
          &::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            border-bottom: rem-calc(1) solid var(--disabled-color-secondary);
            pointer-events: none;

            @include set-breakpoint {
              left: 2.5%;
              right: auto;
              width: 95%;
            }
          }
        }

        &--expanded
          .st-table-item__data-cell--white-status:first-child::before {
          box-shadow: inset rem-calc(1) 0 var(--disabled-color-secondary),
            inset rem-calc(-1) 0 var(--disabled-color-secondary),
            inset 0 rem-calc(1) var(--disabled-color-secondary);
        }

        &:last-child {
          border-bottom: none;
          box-shadow: inset 0 rem-calc(-1) var(--disabled-color-secondary);
        }

        &__chevron {
          display: flex;
          justify-content: center;
          padding: rem-calc(2) 0 rem-calc(20) 0;
          &:focus {
            outline: none;
          }
          &:focus-visible .st-icon {
            border-radius: rem-calc(2);
            outline: rem-calc(2) solid var(--border-color-quaternary);
            outline-offset: rem-calc(2);
          }

          @include set-breakpoint {
            padding: 0 rem-calc(12) 0 0;
            &--expanded-true {
              padding-top: 0 !important;
            }
          }
          &--expanded-true {
            transform: scaleY(-1);
            padding-bottom: 0;
            padding-top: rem-calc(12);
          }

          &--right {
            cursor: pointer;

            .st-icon {
              transform: rotate(-90deg);
            }
          }
        }

        @include set-breakpoint {
          display: table-row;
          border-right: none;

          & > .st-table-item__data-cell:last-child {
            border-right: rem-calc(1) solid var(--disabled-color-secondary);
          }
        }
        &:focus {
          outline: rem-calc(2) solid var(--border-color-quaternary);
          outline-offset: rem-calc(-2);
        }
        &--white {
          background-color: var(--container-color-secondary);
        }
        &--beige {
          background-color: var(--container-color-primary);
        }
      }

      &__expanded-row {
        display: flex;

        & > td {
          box-sizing: border-box;
          display: block;
          position: relative;
          width: 100%;
          border-right: rem-calc(1) solid var(--disabled-color-secondary);
          border-bottom: rem-calc(1) solid var(--disabled-color-secondary);

          &::before {
            content: "";
            position: absolute;
            left: rem-calc(-7);
            top: rem-calc(-1);
            bottom: 0;
            width: rem-calc(8);
            background: var(--status-color);
            border-radius: 0 0 0 rem-calc(4);

            @include set-breakpoint {
              left: rem-calc(-8);
            }
          }

          &.st-table__content__body__expanded-row--white-status::before {
            box-shadow: inset rem-calc(1) 0 var(--disabled-color-secondary),
              inset rem-calc(-1) 0 var(--disabled-color-secondary),
              inset 0 rem-calc(-1) var(--disabled-color-secondary);
          }
        }

        @include set-breakpoint {
          display: table-row;

          & > td {
            display: table-cell;
            width: auto;
          }
        }

        &--last > td {
          border-bottom: none;
        }
      }

      .st-table__content__body {
        &__row--is-danger {
          background-color: var(--danger-color-secondary);
          border-top: rem-calc(1) solid var(--danger-color-primary);
          border-bottom: rem-calc(1) solid var(--danger-color-primary);
          border-right: rem-calc(1) solid var(--danger-color-primary);

          @include set-breakpoint {
            border: none;
            & .st-table-item__data-cell {
              border-top: rem-calc(1) solid var(--danger-color-primary);
              border-bottom: rem-calc(1) solid var(--danger-color-primary);
              &:last-child {
                border-right: rem-calc(1) solid var(--danger-color-primary);
              }

              &__checkbox {
                border-right: rem-calc(1) solid var(--danger-color-primary);
              }
            }
          }
        }
      }
    }
  }
}
</style>
